import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Table, Button, Modal,
  Form, Alert, Spinner, Badge, ProgressBar
} from 'react-bootstrap';
import {
  FaBell, FaEdit, FaTrash, FaPlus, FaFilter, FaDownload,
  FaExclamationTriangle, FaWrench, FaInfoCircle, FaUsers,
  FaCalendarAlt, FaFileAlt, FaFilePdf, FaEye, FaArchive,
  FaCheckCircle, FaClock, FaUpload, FaTimes, FaSync, FaImage
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import './NewsManager.css';
import '../styles/AdminTabs.css';

// ═══════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════
const PRIORITY_CONFIG = {
  emergency: {
    label: 'Emergencia',
    icon: FaExclamationTriangle,
    color: 'danger',
    bgClass: 'priority-emergency'
  },
  maintenance: {
    label: 'Mantenimiento',
    icon: FaWrench,
    color: 'warning',
    bgClass: 'priority-maintenance'
  },
  informative: {
    label: 'Informativo',
    icon: FaInfoCircle,
    color: 'info',
    bgClass: 'priority-informative'
  },
  meeting: {
    label: 'Reunión',
    icon: FaUsers,
    color: 'primary',
    bgClass: 'priority-meeting'
  }
};

const AUDIENCE_CONFIG = {
  all: { label: 'Todos', icon: FaUsers },
  owners: { label: 'Propietarios', icon: FaUsers },
  tenants: { label: 'Inquilinos', icon: FaUsers },
  admins: { label: 'Administradores', icon: FaUsers }
};

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'secondary' },
  published: { label: 'Publicado', color: 'success' },
  archived: { label: 'Archivado', color: 'dark' }
};

const NewsManager = ({ user, onNoticeCreated }) => {
  // ═══════════════════════════════════════════════════════════════
  // ESTADOS
  // ═══════════════════════════════════════════════════════════════
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all'); // all, published, draft, archived
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewNotice, setPreviewNotice] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'informative',
    target_audience: 'all',
    expires_at: '',
    status: 'published',
    attachment: null
  });

  // ═══════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════
  const getToken = () => localStorage.getItem('token') || '';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-EC', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return 'Fecha inválida';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ═══════════════════════════════════════════════════════════════
  // FETCH AVISOS
  // ═══════════════════════════════════════════════════════════════
  const fetchNotices = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) {
        setError('No autorizado. Por favor, inicie sesión de nuevo.');
        setLoading(false);
        return;
      }

      // Construir query params
      let queryParams = new URLSearchParams();
      if (activeTab !== 'all') {
        queryParams.append('status', activeTab);
      }
      if (activeFilter !== 'all') {
        queryParams.append('priority', activeFilter);
      }
      queryParams.append('includeExpired', 'true');

      const url = `${API_ENDPOINTS.notices}?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();
      setNotices(data.notices || []);

    } catch (err) {
      console.error('Error al cargar avisos:', err);
      setError('Error al cargar los avisos. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeFilter]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  // ═══════════════════════════════════════════════════════════════
  // MODAL HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleOpenModal = (notice = null) => {
    if (notice) {
      setIsEditMode(true);
      setSelectedNotice(notice);
      setFormData({
        title: notice.title || '',
        description: notice.description || '',
        priority: notice.priority || 'informative',
        target_audience: notice.target_audience || 'all',
        expires_at: notice.expires_at ? notice.expires_at.split('T')[0] : '',
        status: notice.status || 'published',
        attachment: null
      });
      if (notice.attachment_path) {
        setPreviewFile({
          name: notice.attachment_name,
          size: notice.attachment_size,
          isExisting: true
        });
      } else {
        setPreviewFile(null);
      }
    } else {
      setIsEditMode(false);
      setSelectedNotice(null);
      setFormData({
        title: '',
        description: '',
        priority: 'informative',
        target_audience: 'all',
        expires_at: '',
        status: 'published',
        attachment: null
      });
      setPreviewFile(null);
    }
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setUploadProgress(0);
    setPreviewFile(null);
  };

  // ═══════════════════════════════════════════════════════════════
  // FORM HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError('Tipo de archivo no permitido. Solo PDF, JPG o PNG.');
        return;
      }
      // Validar tamaño (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('El archivo es muy grande. Máximo 10MB.');
        return;
      }

      setFormData(prev => ({ ...prev, attachment: file }));
      setPreviewFile({
        name: file.name,
        size: file.size,
        isExisting: false
      });
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({ ...prev, attachment: null }));
    setPreviewFile(null);
  };

  // ═══════════════════════════════════════════════════════════════
  // SUBMIT (Crear/Editar)
  // ═══════════════════════════════════════════════════════════════
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    const token = getToken();
    if (!token) {
      setError('No autorizado.');
      setLoading(false);
      return;
    }

    try {
      // Usar FormData para soportar archivo
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('priority', formData.priority);
      submitData.append('target_audience', formData.target_audience);
      submitData.append('status', formData.status);

      if (formData.expires_at) {
        submitData.append('expires_at', formData.expires_at);
      }

      if (formData.attachment) {
        submitData.append('attachment', formData.attachment);
      }

      // Si es edición y se quitó el archivo existente
      if (isEditMode && selectedNotice?.attachment_path && !previewFile) {
        submitData.append('remove_attachment', 'true');
      }

      const url = isEditMode
        ? API_ENDPOINTS.noticesById(selectedNotice.id)
        : API_ENDPOINTS.notices;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || `Error ${response.status}`);
      }

      setSuccess(`Aviso ${isEditMode ? 'actualizado' : 'creado'} exitosamente.`);

      // Refrescar lista
      await fetchNotices();

      // Notificar al padre si es creación
      if (!isEditMode && onNoticeCreated && responseData.notice) {
        onNoticeCreated(responseData.notice);
      }

      handleCloseModal();

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Ocurrió un error inesperado.');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // ARCHIVAR AVISO
  // ═══════════════════════════════════════════════════════════════
  const handleArchiveNotice = async (id) => {
    if (!window.confirm('¿Archivar este aviso? Ya no será visible para los usuarios.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.noticesById(id)}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al archivar');
      }

      setSuccess('Aviso archivado exitosamente.');
      await fetchNotices();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // ELIMINAR AVISO
  // ═══════════════════════════════════════════════════════════════
  const handleDeleteNotice = async (id) => {
    if (!window.confirm('¿Eliminar este aviso permanentemente? Esta acción no se puede deshacer.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.noticesById(id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar');
      }

      setSuccess('Aviso eliminado exitosamente.');
      await fetchNotices();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DESCARGAR ADJUNTO
  // ═══════════════════════════════════════════════════════════════
  const handleDownloadAttachment = async (notice) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.noticesById(notice.id)}/download`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) throw new Error('Error al descargar');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = notice.attachment_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error al descargar el archivo');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // FILTRAR AVISOS SEGÚN TAB ACTIVO
  // ═══════════════════════════════════════════════════════════════
  const getFilteredNotices = () => {
    let filtered = [...notices];

    // Filtrar por estado (tab)
    if (activeTab !== 'all') {
      filtered = filtered.filter(n => n.status === activeTab);
    }

    // Filtrar por prioridad
    if (activeFilter !== 'all') {
      filtered = filtered.filter(n => n.priority === activeFilter);
    }

    return filtered;
  };

  const filteredNotices = getFilteredNotices();

  // ═══════════════════════════════════════════════════════════════
  // VISTA PREVIA
  // ═══════════════════════════════════════════════════════════════
  const handlePreviewNotice = async (notice) => {
    setPreviewNotice(notice);

    // Si tiene imagen adjunta, cargarla
    if (notice.attachment_path && isImageFile(notice)) {
      try {
        const response = await fetch(`${API_ENDPOINTS.noticesById(notice.id)}/download`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          setPreviewImageUrl(url);
        }
      } catch (err) {
        console.error('Error loading preview image:', err);
      }
    }

    setShowPreviewModal(true);
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewNotice(null);
    if (previewImageUrl) {
      window.URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl(null);
    }
  };

  const isImageFile = (mimeOrNotice) => {
    // Si es un string (MIME type)
    if (typeof mimeOrNotice === 'string') {
      return mimeOrNotice.startsWith('image/');
    }
    // Si es un objeto (notice)
    if (mimeOrNotice && typeof mimeOrNotice === 'object') {
      const mime = mimeOrNotice.attachment_type || mimeOrNotice.attachment_mime || '';
      if (mime.startsWith('image/')) return true;
      // Fallback por extensión
      const name = (mimeOrNotice.attachment_name || mimeOrNotice.attachment_path || '').toLowerCase();
      return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.gif') || name.endsWith('.webp');
    }
    return false;
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="urbanq-avisos-container">
      {/* ═══════════════════════════════════════════════════════════
          TABS
      ═══════════════════════════════════════════════════════════ */}
      <div className="urbanq-admin-tabs">
        <button
          className={`urbanq-admin-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <FaFileAlt /> Todos ({notices.length})
        </button>
        <button
          className={`urbanq-admin-tab ${activeTab === 'published' ? 'active' : ''}`}
          onClick={() => setActiveTab('published')}
        >
          <FaCheckCircle /> Publicados ({notices.filter(n => n.status === 'published').length})
        </button>
        <button
          className={`urbanq-admin-tab ${activeTab === 'draft' ? 'active' : ''}`}
          onClick={() => setActiveTab('draft')}
        >
          <FaClock /> Borradores ({notices.filter(n => n.status === 'draft').length})
        </button>
        <button
          className={`urbanq-admin-tab ${activeTab === 'archived' ? 'active' : ''}`}
          onClick={() => setActiveTab('archived')}
        >
          <FaArchive /> Archivados ({notices.filter(n => n.status === 'archived').length})
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          CONTENIDO PRINCIPAL
      ═══════════════════════════════════════════════════════════ */}
      <div className="urbanq-avisos-content">
        {/* ═══════════════════════════════════════════════════════════
            CARD PRINCIPAL
        ═══════════════════════════════════════════════════════════ */}
        <Card className="main-card">
          {/* Acciones del header */}
          <Card.Header className="main-card-header">
            <div className="header-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={fetchNotices}
              disabled={loading}
              className="me-2"
            >
              <FaSync className={loading ? 'spin' : ''} />
            </Button>
            <Button
              variant="primary"
              onClick={() => handleOpenModal()}
              disabled={loading}
              className="btn-create"
            >
              <FaPlus className="me-2" />
              Crear Aviso
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {/* Filtros por prioridad */}
          <div className="filters-bar">
            <span className="filter-label">
              <FaFilter className="me-2" />
              Filtrar por:
            </span>
            <div className="filter-buttons">
              <Button
                variant={activeFilter === 'all' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setActiveFilter('all')}
              >
                Todos
              </Button>
              {Object.entries(PRIORITY_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <Button
                    key={key}
                    variant={activeFilter === key ? config.color : 'outline-secondary'}
                    size="sm"
                    onClick={() => setActiveFilter(key)}
                  >
                    <Icon className="me-1" />
                    {config.label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Mensajes de estado */}
          {error && (
            <Alert variant="danger" className="m-3" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="m-3" onClose={() => setSuccess(null)} dismissible>
              {success}
            </Alert>
          )}

          {/* Tabla de avisos */}
          {loading && notices.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Cargando avisos...</p>
            </div>
          ) : filteredNotices.length === 0 ? (
            <div className="empty-state">
              <FaBell className="empty-icon" />
              <h4>No hay avisos</h4>
              <p>No se encontraron avisos con los filtros seleccionados.</p>
              <Button variant="primary" onClick={() => handleOpenModal()}>
                <FaPlus className="me-2" />
                Crear el primer aviso
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="notices-table">
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>Prioridad</th>
                    <th style={{ width: '12%' }}>Fecha</th>
                    <th style={{ width: '25%' }}>Título</th>
                    <th style={{ width: '30%' }}>Descripción</th>
                    <th style={{ width: '8%' }}>Audiencia</th>
                    <th style={{ width: '8%' }}>Estado</th>
                    <th style={{ width: '12%' }} className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotices.map(notice => {
                    const priorityConfig = PRIORITY_CONFIG[notice.priority] || PRIORITY_CONFIG.informative;
                    const PriorityIcon = priorityConfig.icon;
                    const audienceConfig = AUDIENCE_CONFIG[notice.target_audience] || AUDIENCE_CONFIG.all;
                    const statusConfig = STATUS_CONFIG[notice.status] || STATUS_CONFIG.published;

                    return (
                      <tr key={notice.id} className={`notice-row ${priorityConfig.bgClass}`}>
                        <td>
                          <div className={`priority-badge priority-${notice.priority}`}>
                            <PriorityIcon />
                          </div>
                        </td>
                        <td>
                          <div className="date-cell">
                            <FaCalendarAlt className="me-1 text-muted" />
                            {formatDate(notice.created_at)}
                          </div>
                        </td>
                        <td>
                          <div className="title-cell">
                            <span className="notice-title">{notice.title}</span>
                            {notice.attachment_path && (
                              <Badge bg="info" className="ms-2 attachment-badge">
                                <FaFilePdf className="me-1" />
                                PDF
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="description-preview">
                            {notice.description?.length > 80
                              ? `${notice.description.substring(0, 80)}...`
                              : notice.description}
                          </span>
                        </td>
                        <td>
                          <Badge bg="light" text="dark" className="audience-badge">
                            {audienceConfig.label}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handlePreviewNotice(notice)}
                              title="Vista previa"
                            >
                              <FaEye />
                            </Button>
                            {notice.attachment_path && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleDownloadAttachment(notice)}
                                title="Descargar adjunto"
                              >
                                <FaDownload />
                              </Button>
                            )}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenModal(notice)}
                              title="Editar"
                            >
                              <FaEdit />
                            </Button>
                            {notice.status === 'published' && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleArchiveNotice(notice.id)}
                                title="Archivar"
                              >
                                <FaArchive />
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteNotice(notice.id)}
                              title="Eliminar"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      </div>{/* Cierre urbanq-avisos-content */}

      {/* ═══════════════════════════════════════════════════════════
          MODAL CREAR/EDITAR
      ═══════════════════════════════════════════════════════════ */}
      <Modal show={showModal} onHide={handleCloseModal} backdrop="static" size="lg" centered>
        <Modal.Header closeButton className="modal-header-premium">
          <Modal.Title>
            {isEditMode ? (
              <><FaEdit className="me-2" />Editar Aviso</>
            ) : (
              <><FaPlus className="me-2" />Crear Nuevo Aviso</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit} id="noticeForm">
            <Row>
              {/* Título */}
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Título <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Mantenimiento de ascensores programado"
                    disabled={loading}
                  />
                </Form.Group>
              </Col>

              {/* Prioridad y Audiencia */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prioridad <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dirigido a</Form.Label>
                  <Form.Select
                    name="target_audience"
                    value={formData.target_audience}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {Object.entries(AUDIENCE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Descripción */}
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Descripción <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    placeholder="Descripción detallada del aviso..."
                    disabled={loading}
                  />
                </Form.Group>
              </Col>

              {/* Fecha de expiración y Estado */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de expiración (opcional)</Form.Label>
                  <Form.Control
                    type="date"
                    name="expires_at"
                    value={formData.expires_at}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    El aviso se ocultará automáticamente después de esta fecha
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="published">Publicado (visible)</option>
                    <option value="draft">Borrador (no visible)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Archivo adjunto */}
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Archivo adjunto (PDF, JPG, PNG - máx 10MB)</Form.Label>

                  {previewFile ? (
                    <div className="file-preview">
                      <div className="file-info">
                        <FaFilePdf className="file-icon" />
                        <div className="file-details">
                          <span className="file-name">{previewFile.name}</span>
                          <span className="file-size">{formatFileSize(previewFile.size)}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveFile}
                        disabled={loading}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  ) : (
                    <div className="file-upload-zone">
                      <input
                        type="file"
                        id="attachment"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        disabled={loading}
                        className="file-input"
                      />
                      <label htmlFor="attachment" className="file-label">
                        <FaUpload className="upload-icon" />
                        <span>Arrastra un archivo aquí o haz clic para seleccionar</span>
                        <small>PDF, JPG o PNG (máximo 10MB)</small>
                      </label>
                    </div>
                  )}

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <ProgressBar
                      now={uploadProgress}
                      label={`${uploadProgress}%`}
                      className="mt-2"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="noticeForm"
            disabled={loading || !formData.title || !formData.description}
          >
            {loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando...
              </>
            ) : (
              isEditMode ? 'Guardar Cambios' : 'Crear Aviso'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════
          MODAL VISTA PREVIA
      ═══════════════════════════════════════════════════════════ */}
      <Modal
        show={showPreviewModal}
        onHide={handleClosePreviewModal}
        centered
        size="lg"
        className="preview-modal"
      >
        {previewNotice && (
          <>
            <Modal.Header
              closeButton
              className={`preview-modal-header ${PRIORITY_CONFIG[previewNotice.priority]?.bgClass || 'priority-informative'}`}
            >
              <Modal.Title>
                <FaEye className="me-2" />
                Vista Previa del Aviso
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="preview-modal-body">
              {/* Badge de prioridad */}
              <div className="preview-priority-badge mb-3">
                {(() => {
                  const config = PRIORITY_CONFIG[previewNotice.priority] || PRIORITY_CONFIG.informative;
                  const Icon = config.icon;
                  return (
                    <Badge bg={config.color} className="d-flex align-items-center gap-2 px-3 py-2">
                      <Icon />
                      {config.label}
                    </Badge>
                  );
                })()}
                <Badge bg="light" text="dark" className="ms-2">
                  <FaCalendarAlt className="me-1" />
                  {formatDate(previewNotice.created_at)}
                </Badge>
                <Badge bg={STATUS_CONFIG[previewNotice.status]?.color || 'secondary'} className="ms-2">
                  {STATUS_CONFIG[previewNotice.status]?.label || 'Estado'}
                </Badge>
              </div>

              {/* Título */}
              <h4 className="preview-title">{previewNotice.title}</h4>

              {/* Descripción */}
              <div className="preview-description">
                {previewNotice.description}
              </div>

              {/* Archivo adjunto */}
              {previewNotice.attachment_path && (
                <div className="preview-attachment mt-4">
                  <h6 className="attachment-header">
                    {isImageFile(previewNotice) ? (
                      <><FaImage className="me-2 text-success" />Imagen Adjunta</>
                    ) : (
                      <><FaFilePdf className="me-2 text-danger" />Documento Adjunto</>
                    )}
                  </h6>

                  {isImageFile(previewNotice) && previewImageUrl ? (
                    <div className="preview-image-container">
                      <img
                        src={previewImageUrl}
                        alt="Imagen adjunta"
                        className="preview-image"
                      />
                    </div>
                  ) : (
                    <div className="preview-file-info">
                      <FaFilePdf className="file-icon text-danger" />
                      <span>{previewNotice.attachment_name || 'documento.pdf'}</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleDownloadAttachment(previewNotice)}
                      >
                        <FaDownload className="me-1" /> Descargar
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Metadatos */}
              <div className="preview-meta mt-4">
                <small className="text-muted">
                  Dirigido a: <strong>{AUDIENCE_CONFIG[previewNotice.target_audience]?.label || 'Todos'}</strong>
                  {previewNotice.expires_at && (
                    <span className="ms-3">
                      Expira: <strong>{formatDate(previewNotice.expires_at)}</strong>
                    </span>
                  )}
                </small>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-primary" onClick={() => { handleClosePreviewModal(); handleOpenModal(previewNotice); }}>
                <FaEdit className="me-1" /> Editar
              </Button>
              <Button variant="secondary" onClick={handleClosePreviewModal}>
                Cerrar
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default NewsManager;
